/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import React from 'react';

import { toast } from 'react-toastify';

import {
  ListeQueryContext,
  ListeTable,
  ListeTableContainer,
  type ListeType,
} from '@mairistem/liste';
import { request } from '@mairistem/tools';

import type { Diplome } from '@/types';

import DiplomeListeTableRow from './DiplomeListeTableRow';

interface Props {
  /** Nombre de lignes */
  count?: number;
  /** Callback appelé lors du chargement de la liste */
  onLoad?: () => void;
  /** Callback appelé lors de la sélection d'une ligne */
  onSelect?: (qualification: Diplome, qualifications: Diplome[]) => void;
  /** Nombre d'éléments définis par page */
  perPage?: number;
  /** Callback appelé lors d'un clique sur les filtres */
  onSort?: () => void;
  /** Données de la liste */
  items?: Diplome[];
  /** État du chargement */
  loading?: boolean;
  /** Callback appelé lors de la sauvegarde (liste en saisie) */
  onSave?: (item: Diplome) => void;
  /** Diplôme sélectionné */
  selectedDiplome?: Diplome;
  /** Callback appelé lors du reset des items */
  resetSelectedItems?: (item: Diplome) => void;
}

const DiplomeListeTable = (props: Props) => {
  const {
    count = 0,
    perPage = 50,
    items = [],
    selectedDiplome = {},
    loading = false,
    onLoad = null,
    onSelect = null,
    onSort = null,
    onSave = null,
    resetSelectedItems = null,
  } = props;

  const [errors, setErrors] = React.useState([]);

  const { addItem, removeItem, updateItem } =
    React.useContext(ListeQueryContext);

  const deleteDiplomes = React.useCallback(
    (item) => {
      request
        .delete(
          `/api/administres/recensement-citoyen/diplomes/${item.identifiant}`,
        )
        .then(() => {
          removeItem(item);
        })
        .catch(() => {
          toast('Suppression impossible, diplôme lié à un recensé.', {
            type: 'error',
          });
        });
    },
    [removeItem],
  );

  const postDiplomes = React.useCallback(
    (item) => {
      request
        .post('/api/administres/recensement-citoyen/diplomes', item)
        .then((i: ListeType) => {
          addItem(i);
        });
    },
    [addItem],
  );

  const putDiplomes = React.useCallback(
    (item) => {
      request
        .put(
          `/api/administres/recensement-citoyen/diplomes/${item.identifiant}`,
          item,
        )
        .then((i: ListeType) => {
          updateItem(i);
        });
    },
    [updateItem],
  );

  const handleDoubleClick = React.useCallback(
    (diplome: Diplome) => {
      if (onSelect) onSelect(diplome, items);
      if (onSave) onSave(diplome);
    },
    [items, onSave, onSelect],
  );

  const handleSaveDiplome = React.useCallback(
    async (item, status) => {
      if (_.isEmpty(item?.libelle) || _.isEmpty(item?.code)) {
        setErrors(
          _.filter(
            [
              _.isEmpty(item?.libelle)
                ? {
                    field: 'libelle',
                    itemId: item?.identifiant,
                    message: 'Le libelle ne doit pas être vide',
                  }
                : null,
              _.isEmpty(item?.code)
                ? {
                    field: 'code',
                    itemId: item?.identifiant,
                    message: 'Le code ne doit pas être vide',
                  }
                : null,
            ],
            (diplome) => !_.isNil(diplome),
          ),
        );

        return false;
      }

      setErrors(null);

      if (status === 'creating') {
        try {
          await postDiplomes(item);
          return true;
        } catch {
          setErrors([
            {
              itemId: item?.identifiant,
              message: 'Les données doivent être uniques',
            },
          ]);
          return false;
        }
      } else {
        if (item?.identifiant === selectedDiplome?.identifiant) {
          onSave(item);
          resetSelectedItems(item);
        }
        return putDiplomes(item);
      }
    },
    [onSave, selectedDiplome, postDiplomes, putDiplomes, resetSelectedItems],
  );

  const handleAddDiplome = React.useCallback(
    (newItem) => ({
      ...newItem,
      libelle: '',
      code: '',
    }),
    [],
  );

  const handleDeleteDiplome = React.useCallback(
    (item) => {
      if (_.isEqual(item, selectedDiplome)) {
        onSave({});
        resetSelectedItems({});
      }

      deleteDiplomes(item);
    },
    [selectedDiplome, deleteDiplomes, onSave, resetSelectedItems],
  );

  const handleCancelClick = React.useCallback(() => {
    setErrors([]);
  }, []);

  const renderRow = (
    diplome: Diplome,
    _invalidate: any,
    rowProps: any,
    utilities: any,
  ) => (
    <DiplomeListeTableRow
      diplome={diplome}
      rowProps={rowProps}
      utilities={utilities}
      errors={errors}
      onDoubleClick={handleDoubleClick}
    />
  );

  return (
    <ListeTableContainer
      onAddClick={handleAddDiplome}
      onValidateClick={handleSaveDiplome}
      onCancelClick={handleCancelClick}
      onDeleteClick={handleDeleteDiplome}
      additionLabel="Ajouter un nouveau diplôme"
      items={items}
      errors={errors}
      switchToCreationIfEmpty
      allowAdditions
      allowModifications
      allowDeletions
    >
      <ListeTable
        count={count}
        loading={loading}
        onLoad={onLoad}
        onSelect={onSelect}
        perPage={perPage}
        onSort={onSort}
        selection="click"
        selectedItems={_.isEmpty(selectedDiplome) ? [] : [selectedDiplome]}
        showCount
        countLabel={count > 1 ? 'diplômes' : 'diplôme'}
      >
        <ListeTable.Header>
          <ListeTable.Column
            name="code"
            sortable
            width={2}
            only={['m', 'l', 'xl']}
            required
          >
            Code
          </ListeTable.Column>
          <ListeTable.Column
            name="libelle"
            sortable
            width={14}
            only={['s', 'm', 'l', 'xl']}
            required
          >
            Libellé
          </ListeTable.Column>
        </ListeTable.Header>
        {renderRow as unknown as React.ReactNode}
      </ListeTable>
    </ListeTableContainer>
  );
};

export default DiplomeListeTable;
